import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

/**
 * Dynamic form renderer that reads field configuration from the admin form builder.
 * Renders text, number, dropdown, date, checkbox, textarea, file fields.
 *
 * Props:
 * - entityType: "dealer" | "employee" | "order"
 * - formData: object with current values
 * - setFormData: setter function
 * - sectionFilter: optional section name to filter fields (e.g. "business", "bank")
 * - mobileOnly: if true, show only mobile-visible fields
 * - editableOnly: if true, show only employee-editable fields
 */
const DynamicFormRenderer = ({ entityType = "dealer", formData, setFormData, sectionFilter, mobileOnly = false, editableOnly = false }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const url = mobileOnly
        ? `${API}/mobile/form-fields/${entityType}`
        : `${API}/admin/form-fields/${entityType}`;

      // Pull the entity-level visibility / required config that the Admin Panel
      // saves under `module_dealers.dealer_form_fields` etc. so the dialog
      // honors what super-admin configured without code changes.
      const moduleId = entityType === "dealer" ? "dealers" :
                       entityType === "employee" ? "employees" :
                       entityType === "order" ? "orders" : null;
      const cfgUrl = moduleId ? `${API}/admin/module-config/${moduleId}` : null;

      const [res, cfgRes] = await Promise.all([
        authFetch(url),
        cfgUrl ? authFetch(cfgUrl).catch(() => null) : Promise.resolve(null),
      ]);

      let visibleSet = null;
      let requiredSet = null;
      try {
        if (cfgRes && cfgRes.ok) {
          const cfgBody = await cfgRes.json();
          const cfg = cfgBody?.data || {};
          const vis = entityType === "dealer" ? cfg.dealer_form_fields : null;
          const req = entityType === "dealer" ? cfg.dealer_required_fields : null;
          if (Array.isArray(vis) && vis.length > 0) visibleSet = new Set(vis);
          if (Array.isArray(req) && req.length > 0) requiredSet = new Set(req);
        }
      } catch { /* ignore */ }

      const data = await res.json();
      if (data.success) {
        let flds = data.data.filter(f => f.active !== false);
        if (sectionFilter) flds = flds.filter(f => f.section === sectionFilter);
        if (editableOnly) flds = flds.filter(f => f.editable_by_employee);

        // Apply admin-panel visibility filter (only if configured)
        if (visibleSet) flds = flds.filter(f => visibleSet.has(f.field_name));

        // Apply admin-panel required overlay
        if (requiredSet) {
          flds = flds.map(f => requiredSet.has(f.field_name) ? { ...f, required: true } : f);
        }

        setFields(flds);
      }
      setLoading(false);
    })();
  }, [entityType, sectionFilter, mobileOnly, editableOnly]);

  const handleChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  if (loading) return <div className="text-center py-4"><div className="w-5 h-5 spinner mx-auto" /></div>;
  if (fields.length === 0) return null;

  // Group by section
  const sections = {};
  fields.forEach(f => {
    const sec = f.section || "general";
    if (!sections[sec]) sections[sec] = [];
    sections[sec].push(f);
  });

  const sectionLabels = {
    business: "Business Details", owner: "Owner / Proprietor", bank: "Bank Details",
    documents: "Documents", general: "General", custom: "Custom Fields",
  };

  const renderField = (field) => {
    const key = field.field_name;
    const val = formData[key] ?? "";
    const testId = `dynamic-field-${key}`;

    switch (field.field_type) {
      case "dropdown":
        return (
          <Select value={val || ""} onValueChange={v => handleChange(key, v)}>
            <SelectTrigger data-testid={testId}><SelectValue placeholder={field.placeholder || `Select ${field.field_label}`} /></SelectTrigger>
            <SelectContent>
              {(field.options || []).map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      case "number":
        return <Input type="number" value={val} onChange={e => handleChange(key, parseFloat(e.target.value) || 0)} placeholder={field.placeholder} data-testid={testId} />;
      case "date":
        return <Input type="date" value={val} onChange={e => handleChange(key, e.target.value)} data-testid={testId} />;
      case "checkbox":
        return (
          <label className="flex items-center gap-2">
            <Checkbox checked={!!val} onCheckedChange={v => handleChange(key, v)} data-testid={testId} />
            <span className="text-sm text-slate-600">{field.placeholder || field.field_label}</span>
          </label>
        );
      case "textarea":
        return <Textarea value={val} onChange={e => handleChange(key, e.target.value)} placeholder={field.placeholder} rows={3} data-testid={testId} />;
      case "file":
        return <Input type="file" onChange={e => handleChange(key, e.target.files?.[0]?.name || "")} data-testid={testId} />;
      default:
        return <Input value={val} onChange={e => handleChange(key, e.target.value)} placeholder={field.placeholder} data-testid={testId} />;
    }
  };

  const showSectionHeaders = !sectionFilter && Object.keys(sections).length > 1;

  return (
    <div className="space-y-4" data-testid="dynamic-form-renderer">
      {Object.entries(sections).map(([sec, flds]) => (
        <div key={sec}>
          {showSectionHeaders && (
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 mt-3 border-b pb-1 dark:text-slate-400 dark:border-slate-700">
              {sectionLabels[sec] || sec}
            </h4>
          )}
          <div className="grid grid-cols-2 gap-3">
            {flds.map(field => (
              <div key={field.field_id} className={field.field_type === "textarea" ? "col-span-2" : ""}>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">
                  {field.field_label}
                  {field.required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                {renderField(field)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DynamicFormRenderer;
